/**
 * Eldstad favourites (ELD-48, first version)
 * -------------------------------------------
 * localStorage only. No account, no server, no consent gate.
 *
 * Why no consent gate: the 2026-08-18 working session decided that saving a
 * favourite is application function rather than marketing tracking, so the
 * storage it needs is exempt. The ANALYTICS event about the save is not exempt,
 * which is why this file stores unconditionally but only reports through
 * EldstadLadder, which Shopify's consent gate governs.
 *
 * Identity is the product HANDLE, per ELD-47's contract. The grid exposes it as
 * data-product on .card-wrapper, and the PIM keys on it too.
 */
(function () {
  'use strict';

  var KEY = 'eldstad_favourites';
  var EVENT = 'eldstad:favourites-changed';

  /* ---------------------------------------------------------------- store */

  function read() {
    try {
      var raw = JSON.parse(window.localStorage.getItem(KEY));
      return Array.isArray(raw) ? raw.filter(function (h) { return typeof h === 'string' && h; }) : [];
    } catch (err) {
      return []; // private mode, quota, corrupted value. Never break the page.
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list));
    } catch (err) { /* storage unavailable; the UI still works for this pageview */ }
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { list: list } }));
  }

  function has(handle) { return read().indexOf(handle) !== -1; }

  /* ----------------------------------------------------- ladder reporting */

  // ELD-47's contract. Absent until its theme app extension ships, so every
  // call is guarded: instrumentation must never take a favourite down with it.
  function report(method, handle, context) {
    var ladder = window.EldstadLadder;
    if (!ladder || typeof ladder[method] !== 'function') return;
    try {
      ladder[method]({
        product_handle: handle,
        list_size: read().length,
        price: context && context.price,
        category: context && context.category
      });
    } catch (err) { /* never propagate */ }
  }

  /* ------------------------------------------------------------- mutation */

  function add(handle, context) {
    if (!handle || has(handle)) return;
    var list = read();
    list.push(handle);
    write(list);
    report('favouriteAdded', handle, context);
  }

  function remove(handle) {
    var list = read().filter(function (h) { return h !== handle; });
    write(list);
    report('favouriteRemoved', handle);
  }

  /**
   * A product renamed after it was saved still resolves, because Shopify
   * redirects the old handle, but the page it returns is keyed to the new one.
   * Left alone the entry is stranded: its heart reads unsaved, so it cannot be
   * removed, and pressing it saves the same product a second time.
   *
   * Rewrite the stored handle in place so every surface agrees again.
   * Deliberately silent. This is a correction, not a favourite added or
   * removed, and reporting it would put a phantom step on the ladder.
   */
  function migrate(from, to) {
    if (!from || !to || from === to) return false;
    var list = read();
    var i = list.indexOf(from);
    if (i === -1) return false;
    if (list.indexOf(to) === -1) list[i] = to;
    else list.splice(i, 1); // already saved under the new handle, so drop the stale one
    write(list);
    return true;
  }

  function toggle(handle, context) {
    if (has(handle)) { remove(handle); return false; }
    add(handle, context);
    return true;
  }

  /* --------------------------------------------------------------- helpers */

  // Stockholm renders prices as "Från 59 500 kr" with a non-breaking space, and
  // only after its own JS has run. Read it if it is there, skip it if not:
  // ELD-47 accepts a missing price and records price_source accordingly.
  function priceFrom(scope) {
    if (!scope) return undefined;
    var el = scope.querySelector('.price-item, .price__regular .price-item--regular');
    if (!el) return undefined;
    var digits = (el.textContent || '').replace(/[^\d]/g, '');
    if (!digits) return undefined;
    var value = parseInt(digits, 10);
    return isFinite(value) && value > 0 ? value : undefined;
  }

  function label(active) {
    return active ? 'Sparad' : 'Spara';
  }

  function paint(button, active) {
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.classList.toggle('is-active', active);
    var title = button.getAttribute('data-product-title') || 'produkten';
    button.setAttribute(
      'aria-label',
      active ? 'Ta bort ' + title + ' från favoriter' : 'Spara ' + title + ' som favorit'
    );
    var text = button.querySelector('.eldstad-fav__label');
    if (text) text.textContent = label(active);
  }

  /* ----------------------------------------------------------------- wire */

  function wire(root) {
    (root || document).querySelectorAll('.eldstad-fav[data-handle]').forEach(function (button) {
      if (button.dataset.eldstadWired) return;
      button.dataset.eldstadWired = '1';
      paint(button, has(button.dataset.handle));

      button.addEventListener('click', function (event) {
        // Cards are wrapped in links. Without this the browser navigates away
        // the moment you save, which reads as the site being broken.
        event.preventDefault();
        event.stopPropagation();
        var handle = button.dataset.handle;
        var scope = button.closest('.card-wrapper') || button.closest('.product') || document;
        toggle(handle, { price: priceFrom(scope), category: button.dataset.category || undefined });
      });
    });
  }

  // Repaint every button when the list changes anywhere, so two cards for the
  // same product, or a card and the product page, never disagree.
  document.addEventListener(EVENT, function () {
    document.querySelectorAll('.eldstad-fav[data-handle]').forEach(function (b) {
      paint(b, has(b.dataset.handle));
    });
    document.querySelectorAll('[data-eldstad-fav-count]').forEach(function (el) {
      var n = read().length;
      el.textContent = n ? String(n) : '';
      el.hidden = n === 0;
    });
  });

  /* ---------------------------------------------------------------- public */

  window.EldstadFavourites = {
    list: read,
    has: has,
    add: add,
    remove: remove,
    migrate: migrate,
    toggle: toggle,
    wire: wire,
    EVENT: EVENT
  };

  function init() {
    wire(document);
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { list: read() } }));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The grid re-renders on filter, sort and paginate without a page load, so
  // newly inserted cards need wiring. Cheap, and it fails safe if unsupported.
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) { wire(document); return; }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
